import { h } from 'preact';
import { useState, useEffect, useRef } from 'preact/hooks';
import { t } from '../i18n.js';
import { api } from '../state/auth.js';
import Button from '../components/shared/Button.jsx';
import Modal from '../components/shared/Modal.jsx';
import { CategoryWelcomeModal, CommissionContactModal } from '../components/shared/WelcomeSettings.jsx';
import { hasModule } from '../utils/plan.js';
import { useAuth } from '../hooks/useAuth.js';
import DepartmentsCard from '../components/shared/DepartmentsCard.jsx';
import { DepartmentLabel } from '../components/shared/DepartmentLabel.jsx';
import { loadDepartments } from '../utils/departments.js';

export default function SectionsView() {
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Section modals
  const [sectionModal, setSectionModal] = useState(null); // null | { mode: 'create'|'edit', section? }
  const [sectionName, setSectionName] = useState('');
  const [sectionDept, setSectionDept] = useState(''); // '' = main club
  const [savingSection, setSavingSection] = useState(false);
  const [sectionError, setSectionError] = useState(null);
  const [confirmDeleteSection, setConfirmDeleteSection] = useState(null);
  const [deletingSection, setDeletingSection] = useState(false);

  // Commission modals
  const [commModal, setCommModal] = useState(null); // null | { mode: 'create'|'edit', sectionId, commission? }
  const [commName, setCommName] = useState('');
  const [savingComm, setSavingComm] = useState(false);
  const [commError, setCommError] = useState(null);
  const [confirmDeleteComm, setConfirmDeleteComm] = useState(null);
  const [deletingComm, setDeletingComm] = useState(false);

  // Category modals
  const [catModal, setCatModal] = useState(null); // null | { mode: 'create'|'edit', sectionId, commissionId?, cat? }
  const [catName, setCatName] = useState('');
  const [catSupervisesIds, setCatSupervisesIds] = useState([]); // array of commission ids
  const [savingCat, setSavingCat] = useState(false);
  const [catError, setCatError] = useState(null);
  const [confirmDeleteCat, setConfirmDeleteCat] = useState(null);
  const [deletingCat, setDeletingCat] = useState(false);

  // Welkom: welcome text + read-first per function, fixed contact per commission
  const { hasPermission } = useAuth();
  const canSetWelcome = hasPermission('settings', 'update') && hasModule('welcome');
  const [welcomeSettings, setWelcomeSettings] = useState({ categories: {}, commissions: {} });
  const [welcomeCat, setWelcomeCat] = useState(null);
  const [contactComm, setContactComm] = useState(null);

  // Drag-to-reorder state for commissions
  const [draggingComm, setDraggingComm] = useState(null); // { commId, sectionId }
  const dragOverComm = useRef(null);

  // Afdelingen (omnivereniging); reloading also refreshes the labels elsewhere in the app
  const [departments, setDepartments] = useState([]);
  const fetchDepartments = () => loadDepartments({ force: true }).then(v => setDepartments(v.departments));
  const departmentOf = section => departments.find(d => d.id === section.department_id) || null;

  const fetchSections = (showLoader = false) => {
    if (showLoader) setLoading(true);
    api('/api/settings/sections')
      .then(setSections)
      .catch(() => setError('Kan secties niet laden.'))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchSections(true);
    fetchDepartments();
    if (canSetWelcome) api('/api/welcome/settings').then(setWelcomeSettings).catch(() => {});
  }, []);
  const welcomeSet = cat => !!(welcomeSettings.categories[cat.id]?.message || welcomeSettings.categories[cat.id]?.documents?.length);

  // ── Section CRUD ─────────────────────────────────────────────────────────────

  const openCreateSection = () => {
    setSectionName('');
    setSectionDept('');
    setSectionError(null);
    setSectionModal({ mode: 'create' });
  };

  const openEditSection = (section) => {
    setSectionName(section.name);
    setSectionDept(section.department_id ? String(section.department_id) : '');
    setSectionError(null);
    setSectionModal({ mode: 'edit', section });
  };

  const handleSaveSection = async () => {
    if (!sectionName.trim()) { setSectionError('Naam is verplicht'); return; }
    setSavingSection(true);
    setSectionError(null);
    try {
      const departmentId = sectionDept ? Number(sectionDept) : null;
      if (sectionModal.mode === 'create') {
        const newSection = await api('/api/settings/sections', { method: 'POST', body: JSON.stringify({ name: sectionName, departmentId }) });
        setSections(prev => [...prev, { ...newSection, commissions: [], categories: [] }]);
      } else {
        await api(`/api/settings/sections/${sectionModal.section.id}`, { method: 'PUT', body: JSON.stringify({ name: sectionName, departmentId }) });
        setSections(prev => prev.map(s => s.id === sectionModal.section.id ? { ...s, name: sectionName, department_id: departmentId } : s));
      }
      setSectionModal(null);
      if (departments.length) fetchDepartments();
    } catch (err) {
      setSectionError(err.code === 'name_exists' ? 'Deze naam bestaat al.' : 'Opslaan mislukt.');
    } finally {
      setSavingSection(false);
    }
  };

  const handleDeleteSection = async () => {
    if (!confirmDeleteSection) return;
    setDeletingSection(true);
    try {
      await api(`/api/settings/sections/${confirmDeleteSection.id}`, { method: 'DELETE' });
      setSections(prev => prev.filter(s => s.id !== confirmDeleteSection.id));
      setConfirmDeleteSection(null);
    } catch {
      // intentionally empty
    } finally {
      setDeletingSection(false);
    }
  };

  // ── Commission CRUD ───────────────────────────────────────────────────────────

  const openCreateComm = (sectionId) => {
    setCommName('');
    setCommError(null);
    setCommModal({ mode: 'create', sectionId });
  };

  const openEditComm = (commission, sectionId) => {
    setCommName(commission.name);
    setCommError(null);
    setCommModal({ mode: 'edit', sectionId, commission });
  };

  const handleSaveComm = async () => {
    if (!commName.trim()) { setCommError('Naam is verplicht'); return; }
    setSavingComm(true);
    setCommError(null);
    try {
      if (commModal.mode === 'create') {
        const newComm = await api('/api/settings/commissions', { method: 'POST', body: JSON.stringify({ name: commName, sectionId: commModal.sectionId }) });
        setSections(prev => prev.map(s =>
          s.id === commModal.sectionId
            ? { ...s, commissions: [...(s.commissions || []), { ...newComm, categories: [] }] }
            : s
        ));
      } else {
        await api(`/api/settings/commissions/${commModal.commission.id}`, { method: 'PUT', body: JSON.stringify({ name: commName }) });
        setSections(prev => prev.map(s => ({
          ...s,
          commissions: (s.commissions || []).map(c =>
            c.id === commModal.commission.id ? { ...c, name: commName } : c
          )
        })));
      }
      setCommModal(null);
    } catch (err) {
      setCommError(err.code === 'name_exists' ? 'Deze naam bestaat al.' : 'Opslaan mislukt.');
    } finally {
      setSavingComm(false);
    }
  };

  const handleDeleteComm = async () => {
    if (!confirmDeleteComm) return;
    setDeletingComm(true);
    try {
      await api(`/api/settings/commissions/${confirmDeleteComm.id}`, { method: 'DELETE' });
      setSections(prev => prev.map(s => ({
        ...s,
        commissions: (s.commissions || []).filter(c => c.id !== confirmDeleteComm.id)
      })));
      setConfirmDeleteComm(null);
    } catch {
      // intentionally empty
    } finally {
      setDeletingComm(false);
    }
  };

  // ── Commission drag-to-reorder ────────────────────────────────────────────────

  const handleCommDragStart = (commId, sectionId) => {
    setDraggingComm({ commId, sectionId });
  };

  const handleCommDragOver = (e, commId) => {
    e.preventDefault();
    dragOverComm.current = commId;
  };

  const handleCommDrop = (sectionId) => {
    if (!draggingComm || draggingComm.commId === dragOverComm.current) {
      setDraggingComm(null);
      return;
    }
    setSections(prev => prev.map(s => {
      if (s.id !== sectionId) return s;
      const comms = [...s.commissions];
      const fromIdx = comms.findIndex(c => c.id === draggingComm.commId);
      const toIdx = comms.findIndex(c => c.id === dragOverComm.current);
      if (fromIdx === -1 || toIdx === -1) return s;
      const [moved] = comms.splice(fromIdx, 1);
      comms.splice(toIdx, 0, moved);
      // Persist sort_orders in background — no state re-fetch
      Promise.all(comms.map((c, i) =>
        api(`/api/settings/commissions/${c.id}`, { method: 'PUT', body: JSON.stringify({ name: c.name, sortOrder: i + 1 }) })
      ));
      return { ...s, commissions: comms };
    }));
    setDraggingComm(null);
  };

  // ── Category CRUD ─────────────────────────────────────────────────────────────

  const openCreateCat = (sectionId, commissionId = null) => {
    setCatName('');
    setCatSupervisesIds([]);
    setCatError(null);
    setCatModal({ mode: 'create', sectionId, commissionId });
  };

  const openEditCat = (cat, sectionId) => {
    setCatName(cat.name);
    setCatSupervisesIds(cat.supervises_commission_ids || []);
    setCatError(null);
    setCatModal({ mode: 'edit', sectionId, cat });
  };

  const handleSaveCat = async () => {
    if (!catName.trim()) { setCatError('Naam is verplicht'); return; }
    setSavingCat(true);
    setCatError(null);
    try {
      if (catModal.mode === 'create') {
        const newCat = await api('/api/settings/categories', { method: 'POST', body: JSON.stringify({ name: catName, sectionId: catModal.sectionId, commissionId: catModal.commissionId || null }) });
        setSections(prev => prev.map(s => {
          if (s.id !== catModal.sectionId) return s;
          if (catModal.commissionId) {
            return { ...s, commissions: s.commissions.map(c =>
              c.id === catModal.commissionId ? { ...c, categories: [...(c.categories || []), newCat] } : c
            )};
          }
          return { ...s, categories: [...(s.categories || []), newCat] };
        }));
      } else {
        await api(`/api/settings/categories/${catModal.cat.id}`, { method: 'PUT', body: JSON.stringify({
          name: catName,
          supervisesCommissionIds: catSupervisesIds,
        }) });
        const updateCat = cats => (cats || []).map(c =>
          c.id === catModal.cat.id ? { ...c, name: catName, supervises_commission_ids: catSupervisesIds } : c
        );
        setSections(prev => prev.map(s => ({
          ...s,
          categories: updateCat(s.categories),
          commissions: s.commissions.map(comm => ({ ...comm, categories: updateCat(comm.categories) }))
        })));
      }
      setCatModal(null);
    } catch (err) {
      setCatError(err.code === 'name_exists' ? 'Deze naam bestaat al.' : 'Opslaan mislukt.');
    } finally {
      setSavingCat(false);
    }
  };

  const handleDeleteCat = async () => {
    if (!confirmDeleteCat) return;
    setDeletingCat(true);
    try {
      await api(`/api/settings/categories/${confirmDeleteCat.id}`, { method: 'DELETE' });
      const id = confirmDeleteCat.id;
      setSections(prev => prev.map(s => ({
        ...s,
        categories: (s.categories || []).filter(c => c.id !== id),
        commissions: (s.commissions || []).map(comm => ({
          ...comm,
          categories: (comm.categories || []).filter(c => c.id !== id)
        }))
      })));
      setConfirmDeleteCat(null);
    } catch {
      // intentionally empty
    } finally {
      setDeletingCat(false);
    }
  };

  // For edit modal: all commissions of the section
  const commPickerOptions = catModal?.mode === 'edit'
    ? (sections.find(s => s.id === catModal.sectionId)?.commissions || [])
    : [];

  return (
    <div>
      <div class="page-header">
        <h1 class="page-title">Secties &amp; functies</h1>
        <Button variant="primary" onClick={openCreateSection}>+ Sectie toevoegen</Button>
      </div>

      {error && <div class="alert alert-error mt-4">{error}</div>}

      {!loading && (
        <DepartmentsCard departments={departments} sections={sections}
          onChanged={() => { fetchDepartments(); fetchSections(); }} />
      )}

      {loading ? (
        <div style="display:flex;flex-direction:column;gap:16px;margin-top:16px">
          {[1,2,3].map(i => (
            <div key={i} class="card" style="padding:16px">
              <div class="skeleton" style="height:18px;width:30%;margin-bottom:12px;border-radius:4px" />
              <div class="skeleton" style="height:12px;width:60%;border-radius:4px" />
            </div>
          ))}
        </div>
      ) : sections.length === 0 ? (
        <div style="text-align:center;padding:80px 0;color:var(--color-text-muted)">
          <div style="font-size:36px;margin-bottom:12px">📂</div>
          <p>Nog geen secties aangemaakt.</p>
          <Button variant="primary" style="margin-top:16px" onClick={openCreateSection}>Eerste sectie aanmaken</Button>
        </div>
      ) : (
        <div style="display:flex;flex-direction:column;gap:16px;margin-top:16px">
          {sections.map(section => (
            <div key={section.id} class="card" style="padding:0;overflow:hidden">
              {/* Section header */}
              {(() => {
                const totalCats = (section.categories || []).length +
                  (section.commissions || []).reduce((s, c) => s + (c.categories || []).length, 0);
                return (
                  <div style="display:flex;align-items:center;justify-content:space-between;padding:14px 16px;background:var(--color-bg-secondary);border-bottom:1px solid var(--color-border)">
                    <div style="display:flex;align-items:center;gap:8px;flex-wrap:wrap;min-width:0">
                      <div style="font-weight:700;font-size:15px">{section.name}</div>
                      <DepartmentLabel department={departmentOf(section)} />
                    </div>
                    <div style="display:flex;gap:8px;align-items:center">
                      <span style="font-size:12px;color:var(--color-text-muted)">
                        {(section.commissions || []).length > 0
                          ? `${(section.commissions || []).length} commissies · ${totalCats} functies`
                          : `${totalCats} functies`}
                      </span>
                      <Button variant="secondary" size="sm" onClick={() => openEditSection(section)}>Bewerken</Button>
                      <Button variant="danger" size="sm" onClick={() => setConfirmDeleteSection(section)}>Verwijderen</Button>
                    </div>
                  </div>
                );
              })()}

              {/* Body: direct categories + commissions with their categories */}
              <div style="padding:12px 16px">
                {/* Direct categories (e.g. Dagelijks Bestuur) */}
                {(section.categories || []).length > 0 && (
                  <div style="display:flex;flex-wrap:wrap;gap:8px;margin-bottom:12px">
                    {section.categories.map(cat => (
                      <div key={cat.id} style="display:flex;align-items:center;gap:6px;background:var(--color-bg-secondary);border:1px solid var(--color-border);border-radius:20px;padding:4px 10px 4px 12px;font-size:13px">
                        <span>{cat.name}</span>
                        {canSetWelcome && <button style={`border:none;background:none;cursor:pointer;padding:0 2px;font-size:13px;line-height:1;display:flex;align-items:center;opacity:${welcomeSet(cat) ? 1 : 0.45}`} title={welcomeSet(cat) ? 'Welkom (ingesteld)' : 'Welkom instellen'} aria-label={`Welkom voor ${cat.name}`} onClick={() => setWelcomeCat(cat)}>👋</button>}
                        <button style="border:none;background:none;cursor:pointer;color:var(--color-text-muted);padding:0 2px;font-size:13px;line-height:1;display:flex;align-items:center" title="Bewerken" onClick={() => openEditCat(cat, section.id)}>✏️</button>
                        <button style="border:none;background:none;cursor:pointer;color:var(--color-danger,#dc2626);padding:0 2px;font-size:13px;line-height:1;display:flex;align-items:center" title="Verwijderen" onClick={() => setConfirmDeleteCat(cat)}>✕</button>
                      </div>
                    ))}
                  </div>
                )}

                {/* Commissions with sub-categories */}
                {(section.commissions || []).map(commission => (
                  <div
                    key={commission.id}
                    draggable
                    onDragStart={() => handleCommDragStart(commission.id, section.id)}
                    onDragOver={e => handleCommDragOver(e, commission.id)}
                    onDrop={() => handleCommDrop(section.id)}
                    onDragEnd={() => setDraggingComm(null)}
                    style={`margin-bottom:14px;border:1px solid var(--color-border);border-radius:8px;overflow:hidden;opacity:${draggingComm?.commId === commission.id ? 0.4 : 1};transition:opacity .15s`}
                  >
                    <div style="background:var(--color-bg-secondary);padding:8px 12px;font-size:12px;font-weight:600;color:var(--color-text-secondary,var(--color-text-muted));border-bottom:1px solid var(--color-border);display:flex;align-items:center;justify-content:space-between;gap:8px;flex-wrap:wrap;cursor:grab">
                      <div style="display:flex;align-items:center;gap:8px">
                        <span style="color:var(--color-text-muted);font-size:14px;line-height:1">⠿</span>
                        <span>{commission.name}</span>
                      </div>
                      <div style="display:flex;gap:6px;flex-wrap:wrap;justify-content:flex-end">
                        {canSetWelcome && (
                          <Button variant="secondary" size="sm" onClick={() => setContactComm(commission)} title="Aanspreekpunt voor nieuwe leden">
                            👤 {welcomeSettings.commissions[commission.id]?.name?.split(' ')[0] || 'Aanspreekpunt'}
                          </Button>
                        )}
                        <Button variant="secondary" size="sm" onClick={() => openEditComm(commission, section.id)}>Bewerken</Button>
                        <Button variant="danger" size="sm" onClick={() => setConfirmDeleteComm(commission)}>Verwijderen</Button>
                      </div>
                    </div>
                    <div style="padding:10px 12px">
                      {(commission.categories || []).length === 0 ? (
                        <p style="font-size:12px;color:var(--color-text-muted);margin:0 0 8px">Nog geen functies.</p>
                      ) : (
                        <div style="display:flex;flex-wrap:wrap;gap:6px;margin-bottom:8px">
                          {commission.categories.map(cat => (
                            <div key={cat.id} style="display:flex;align-items:center;gap:5px;background:var(--color-bg-secondary);border:1px solid var(--color-border);border-radius:20px;padding:3px 10px 3px 10px;font-size:12px">
                              <span>{cat.name}</span>
                              {canSetWelcome && <button style={`border:none;background:none;cursor:pointer;padding:0 2px;font-size:12px;line-height:1;opacity:${welcomeSet(cat) ? 1 : 0.45}`} title={welcomeSet(cat) ? 'Welkom (ingesteld)' : 'Welkom instellen'} aria-label={`Welkom voor ${cat.name}`} onClick={() => setWelcomeCat(cat)}>👋</button>}
                              <button style="border:none;background:none;cursor:pointer;color:var(--color-text-muted);padding:0 2px;font-size:12px;line-height:1" title="Bewerken" onClick={() => openEditCat(cat, section.id)}>✏️</button>
                              <button style="border:none;background:none;cursor:pointer;color:var(--color-danger,#dc2626);padding:0 2px;font-size:12px;line-height:1" title="Verwijderen" onClick={() => setConfirmDeleteCat(cat)}>✕</button>
                            </div>
                          ))}
                        </div>
                      )}
                      <Button variant="secondary" size="sm" onClick={() => openCreateCat(section.id, commission.id)}>
                        + Functie toevoegen
                      </Button>
                    </div>
                  </div>
                ))}

                {/* No content at all */}
                {(section.categories || []).length === 0 && (section.commissions || []).length === 0 && (
                  <p style="font-size:13px;color:var(--color-text-muted);margin:0 0 10px">Nog geen functies.</p>
                )}

                <div style="display:flex;gap:8px;flex-wrap:wrap">
                  <Button variant="secondary" size="sm" onClick={() => openCreateComm(section.id)}>
                    + Commissie toevoegen
                  </Button>
                  <Button variant="secondary" size="sm" onClick={() => openCreateCat(section.id)}>
                    + Functie toevoegen
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Welkom settings */}
      <CategoryWelcomeModal cat={welcomeCat} current={welcomeCat ? welcomeSettings.categories[welcomeCat.id] : null}
        onClose={() => setWelcomeCat(null)}
        onSaved={saved => { setWelcomeSettings(w => ({ ...w, categories: { ...w.categories, [welcomeCat.id]: saved } })); setWelcomeCat(null); }} />
      <CommissionContactModal commission={contactComm} current={contactComm ? welcomeSettings.commissions[contactComm.id] : null}
        onClose={() => setContactComm(null)}
        onSaved={contact => {
          setWelcomeSettings(w => {
            const commissions = { ...w.commissions };
            if (contact) commissions[contactComm.id] = contact; else delete commissions[contactComm.id];
            return { ...w, commissions };
          });
          setContactComm(null);
        }} />

      {/* Section modal */}
      <Modal
        isOpen={!!sectionModal}
        title={sectionModal?.mode === 'create' ? 'Nieuwe sectie' : 'Sectie bewerken'}
        onClose={() => setSectionModal(null)}
      >
        <div class="form-group">
          <label class="form-label">Naam *</label>
          <input
            class="form-input"
            type="text"
            value={sectionName}
            onInput={e => setSectionName(e.target.value)}
            placeholder="bijv. Technische Commissie"
            autoFocus
          />
        </div>
        {departments.length > 0 && (
          <div class="form-group" style="margin-top:14px">
            <label class="form-label">Afdeling</label>
            <select class="form-input" value={sectionDept} onChange={e => setSectionDept(e.target.value)}>
              <option value="">Hoofdvereniging (geen afdeling)</option>
              {departments.map(d => <option key={d.id} value={String(d.id)}>{d.name}</option>)}
            </select>
          </div>
        )}
        {sectionError && <div class="alert alert-error mb-3">{sectionError}</div>}
        <div class="flex gap-2 justify-end mt-4">
          <Button variant="secondary" onClick={() => setSectionModal(null)}>{t('common.cancel')}</Button>
          <Button variant="primary" onClick={handleSaveSection} disabled={savingSection}>
            {savingSection ? t('common.loading') : t('common.save')}
          </Button>
        </div>
      </Modal>

      {/* Category modal */}
      <Modal
        isOpen={!!catModal}
        title={catModal?.mode === 'create' ? 'Nieuwe functie' : 'Functie bewerken'}
        onClose={() => setCatModal(null)}
      >
        <div class="form-group">
          <label class="form-label">Naam *</label>
          <input
            class="form-input"
            type="text"
            value={catName}
            onInput={e => setCatName(e.target.value)}
            placeholder="bijv. Jeugdcoördinator"
            autoFocus
          />
        </div>
        {commPickerOptions.length > 0 && (
          <div class="form-group" style="margin-top:14px">
            <label class="form-label">Verantwoordelijk voor commissie(s)</label>
            <div style="display:flex;flex-direction:column;gap:8px;margin-top:6px">
              {commPickerOptions.map(c => {
                const checked = catSupervisesIds.includes(c.id);
                return (
                  <label key={c.id} style="display:flex;align-items:center;gap:10px;cursor:pointer;font-size:13px">
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => setCatSupervisesIds(prev =>
                        checked ? prev.filter(id => id !== c.id) : [...prev, c.id]
                      )}
                      style="width:16px;height:16px;cursor:pointer;flex-shrink:0"
                    />
                    {c.name}
                  </label>
                );
              })}
            </div>
            <p style="font-size:11px;color:var(--color-text-muted);margin-top:6px">
              Vink aan voor welke commissies deze rol verantwoordelijk is. Zichtbaar in het organisatieschema.
            </p>
          </div>
        )}
        {catError && <div class="alert alert-error mb-3">{catError}</div>}
        <div class="flex gap-2 justify-end mt-4">
          <Button variant="secondary" onClick={() => setCatModal(null)}>{t('common.cancel')}</Button>
          <Button variant="primary" onClick={handleSaveCat} disabled={savingCat}>
            {savingCat ? t('common.loading') : t('common.save')}
          </Button>
        </div>
      </Modal>

      {/* Delete section confirm */}
      <Modal isOpen={!!confirmDeleteSection} title="Sectie verwijderen" onClose={() => setConfirmDeleteSection(null)}>
        <p style="margin-bottom:20px;color:var(--color-text-muted)">
          Weet je zeker dat je sectie <strong>{confirmDeleteSection?.name}</strong> en alles wat eronder hangt wilt verwijderen?
        </p>
        <div class="flex gap-2 justify-end">
          <Button variant="secondary" onClick={() => setConfirmDeleteSection(null)}>{t('common.cancel')}</Button>
          <Button variant="danger" onClick={handleDeleteSection} disabled={deletingSection}>
            {deletingSection ? t('common.loading') : t('common.delete')}
          </Button>
        </div>
      </Modal>

      {/* Delete category confirm */}
      <Modal isOpen={!!confirmDeleteCat} title="Functie verwijderen" onClose={() => setConfirmDeleteCat(null)}>
        <p style="margin-bottom:20px;color:var(--color-text-muted)">
          Weet je zeker dat je functie <strong>{confirmDeleteCat?.name}</strong> wilt verwijderen?
        </p>
        <div class="flex gap-2 justify-end">
          <Button variant="secondary" onClick={() => setConfirmDeleteCat(null)}>{t('common.cancel')}</Button>
          <Button variant="danger" onClick={handleDeleteCat} disabled={deletingCat}>
            {deletingCat ? t('common.loading') : t('common.delete')}
          </Button>
        </div>
      </Modal>

      {/* Commission modal */}
      <Modal
        isOpen={!!commModal}
        title={commModal?.mode === 'create' ? 'Nieuwe commissie' : 'Commissie bewerken'}
        onClose={() => setCommModal(null)}
      >
        <div class="form-group">
          <label class="form-label">Naam *</label>
          <input
            class="form-input"
            type="text"
            value={commName}
            onInput={e => setCommName(e.target.value)}
            placeholder="bijv. Sectiebestuur Financiën"
            autoFocus
          />
        </div>
        {commError && <div class="alert alert-error mb-3">{commError}</div>}
        <div class="flex gap-2 justify-end mt-4">
          <Button variant="secondary" onClick={() => setCommModal(null)}>{t('common.cancel')}</Button>
          <Button variant="primary" onClick={handleSaveComm} disabled={savingComm}>
            {savingComm ? t('common.loading') : t('common.save')}
          </Button>
        </div>
      </Modal>

      {/* Delete commission confirm */}
      <Modal isOpen={!!confirmDeleteComm} title="Commissie verwijderen" onClose={() => setConfirmDeleteComm(null)}>
        <p style="margin-bottom:20px;color:var(--color-text-muted)">
          Weet je zeker dat je commissie <strong>{confirmDeleteComm?.name}</strong> en alles wat eronder hangt wilt verwijderen?
        </p>
        <div class="flex gap-2 justify-end">
          <Button variant="secondary" onClick={() => setConfirmDeleteComm(null)}>{t('common.cancel')}</Button>
          <Button variant="danger" onClick={handleDeleteComm} disabled={deletingComm}>
            {deletingComm ? t('common.loading') : t('common.delete')}
          </Button>
        </div>
      </Modal>
    </div>
  );
}
